import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAssessmentStatus } from "@/hooks/useAssessmentStatus";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Brain, CheckCircle2, BarChart3, Users, Play, RefreshCw, LayoutDashboard } from "lucide-react";
import digcompFramework from "@/assets/digcomp-framework.png";
import { useToast } from "@/hooks/use-toast";

const Index = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { activeRun, latestAttempt, loading, refreshStatus } = useAssessmentStatus();
  const { toast } = useToast();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-accent/10">

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-6 md:py-10">
        {/* Welcome Section with Username */}
        {user && (
          <Card className="max-w-4xl mx-auto p-4 mb-4 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/30">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                  Welcome, <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">{profile?.first_name || user.email?.split('@')[0] || 'User'}</span>!
                </h2>
                <p className="text-muted-foreground">
                  Ready to assess your digital competence?
                </p>
              </div>

              {!loading && activeRun && (
                <div className="flex gap-2">
                  {(!latestAttempt || latestAttempt.responseCount === 0) ? (
                    <Button
                      size="lg"
                      onClick={() => navigate('/assessment')}
                      className="gap-2 px-8 py-6 text-lg shadow-strong"
                    >
                      <Brain className="h-6 w-6" />
                      Start Assessment
                    </Button>
                  ) : latestAttempt.completed ? (
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => navigate('/dashboard')}
                      className="gap-2 px-8 py-6 text-lg shadow-strong"
                    >
                      <LayoutDashboard className="h-6 w-6" />
                      View Results
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="lg"
                        onClick={() => navigate('/assessment')}
                        className="gap-2 px-8 py-6 text-lg shadow-strong bg-green-600 hover:bg-green-700"
                      >
                        <Play className="h-6 w-6" />
                        Continue
                      </Button>

                    </>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Bilingual Instructions */}
        <Card className="max-w-4xl mx-auto p-4 mb-4 bg-gradient-to-r from-primary/5 to-accent/5 border-primary/30 shadow-lg">
          <div className="space-y-4">
            <p className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
              <strong className="text-primary text-xl md:text-2xl">Welcome!</strong> This assessment will help you evaluate your digital competence across five key areas. Click "Start Assessment" to begin your journey.
            </p>
            <p className="text-lg md:text-xl font-semibold text-foreground font-tajawal leading-relaxed" dir="rtl">
              <strong className="text-primary text-xl md:text-2xl">مرحباً!</strong> ستساعدكم هذه المنصة على قياس كفاءاتكم الرقمية عبر خمسة مجالات رئيسية. اضغطوا على زر بدء المنصة لبدء رحلتكم.
            </p>
          </div>
        </Card>

        <div className="max-w-4xl mx-auto text-center mb-8">
          {/* YouTube Video Embed */}
          <div className="mb-4 flex justify-center">
            <div className="w-full max-w-3xl aspect-video rounded-lg overflow-hidden shadow-strong">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/CsUj06wTCGg"
                title="DigComp Introduction Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          </div>


          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Brain className="h-5 w-5 text-primary" />
            <span className="text-sm font-semibold text-primary">DigComp 3.0 Framework | إطار DigComp 3.0</span>
          </div>

          {/* Login Button Centered Under Video */}
          {!user && (
            <Button
              size="lg"
              className="mb-4 px-8 py-4 text-lg font-semibold shadow-strong hover:scale-105 transition-transform"
              onClick={() => navigate('/login')}
            >
              Login
            </Button>
          )}

          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-2 leading-tight">
            Assess Your Digital
            <span className="block bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Competence
            </span>
          </h1>

          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 leading-tight font-tajawal" dir="rtl">
            قيّم كفاءتك الرقمية
          </h2>

          <div className="space-y-2 mb-4 max-w-2xl mx-auto">
            <p className="text-lg text-muted-foreground leading-relaxed">
              Discover your strengths across five key competence areas with our comprehensive
              24-question assessment based on the European DigComp framework.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed font-tajawal" dir="rtl">
              اكتشف نقاط قوتك عبر خمسة مجالات كفاءة رئيسية من خلال تقييمنا الشامل المكون من 24 سؤالاً بناءً على إطار DigComp الأوروبي.
            </p>
          </div>

          {/* DigComp Framework Image */}
          <div className="flex justify-center mb-4">
            <img
              src={digcompFramework}
              alt="DigComp Framework"
              className="w-64 h-auto"
            />
          </div>

          {/* Copyright Notice */}
          <div className="text-xs text-muted-foreground text-center mt-6 max-w-4xl mx-auto space-y-4 px-4 border-t border-border/50 pt-6">
            <div className="space-y-1">
              <strong className="block text-sm font-semibold mb-1">Copyright Notice (English)</strong>
              <p className="leading-relaxed">
                This assessment tool is based on and incorporates adapted content derived from DigComp 3.0 – the Digital Competence Framework for Citizens, developed by the European Commission. © European Union for original DigComp materials, which are reused in accordance with the European Commission’s reuse policy and, where applicable, under the Creative Commons Attribution 4.0 International (CC BY 4.0) licence. The adaptation, structure, assessment design, and additional original content are © Palestine Polytechnic University (PPU) 2026. Reuse is permitted with proper attribution and without implying endorsement by the European Union or PPU. The content is provided “as is”, without warranties of any kind, and responsibility for its application rests solely with the user. Any reuse must clearly indicate whether changes were made to the original content.
              </p>
            </div>
            <div className="space-y-1" dir="rtl">
              <strong className="block text-sm font-semibold font-tajawal mb-1">حقوق النشر العربية</strong>
              <p className="leading-relaxed font-tajawal">
                تعتمد أداة التقييم هذه على محتوى مُكيَّف ومُستمد من إطار الكفاءات الرقمية للمواطنين DigComp 3.0، الذي طورته المفوضية الأوروبية. حقوق DigComp الأصلية محفوظة للإتحاد الأوروبي ومؤسساته، والتي أُعيد استخدامها وفق سياسة إعادة الاستخدام المعتمدة لدى المفوضية الأوروبية، وحيثما ينطبق، بموجب رخصة المشاع الإبداعي –  (CC BY 4.0). تعود حقوق التكييف، والبنية، وتصميم التقييم، والمحتوى الأصلي الإضافي إلى جامعة بوليتكنك فلسطين (PPU) © 2026. يُسمح بإعادة الاستخدام مع الإشارة الواضحة للمصدر ودون الإيحاء بأي مصادقة رسمية. يُقدَّم المحتوى “كما هو” دون أي ضمانات، وتقع مسؤولية تطبيقه بالكامل على المستخدم، مع ضرورة توضيح أي تعديلات عند إعادة الاستخدام.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="h-14 flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : !activeRun ? (
            user && (
              <div className="bg-accent/10 p-4 rounded-lg border border-accent/20 text-center">
                <p className="text-accent font-semibold">No active assessment available at this time.</p>
                <p className="text-accent font-tajawal text-sm mt-1" dir="rtl">لا يوجد تقييم نشط في الوقت الحالي.</p>
              </div>
            )
          ) : latestAttempt?.completed ? (
            <div className="bg-success/10 p-4 rounded-lg border border-success/20 text-center">
              <p className="text-success font-semibold">You have already completed the current assessment.</p>
              <p className="text-success font-tajawal text-sm mt-1" dir="rtl">لقد أكملت التقييم الحالي بالفعل.</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate("/dashboard")}
              >
                View Results | عرض النتائج
              </Button>
            </div>
          ) : (
            <>
              <Button
                size="lg"
                onClick={() => navigate("/assessment")}
                className={`h-14 px-8 text-lg font-semibold shadow-strong hover:scale-105 transition-transform ${(latestAttempt && latestAttempt.responseCount > 0) ? 'bg-green-600 hover:bg-green-700' : ''}`}
              >
                {(latestAttempt && latestAttempt.responseCount > 0) ? 'Continue Assessment | متابعة التقييم' : 'Start Assessment | بدء التقييم'}
              </Button>
              <p className="text-sm text-muted-foreground mt-4">
                ⏱️ Takes approximately 10 minutes | يستغرق حوالي 10 دقائق
              </p>
            </>
          )}
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-16">
          {[
            {
              icon: CheckCircle2,
              title: "24 Questions",
              titleAr: "24 سؤال",
              description: "Comprehensive assessment covering all competence areas",
              descriptionAr: "تقييم شامل يغطي جميع مجالات الكفاءة"
            },
            {
              icon: Brain,
              title: "5 Core Areas",
              titleAr: "5 مجالات أساسية",
              description: "Evaluate Information, Communication, Creation, Safety & Problem Solving",
              descriptionAr: "تقييم المعلومات والتواصل والإبداع والسلامة وحل المشكلات"
            },
            {
              icon: BarChart3,
              title: "Visual Reports",
              titleAr: "تقارير مرئية",
              description: "Get detailed insights with charts and personalized recommendations",
              descriptionAr: "احصل على رؤى تفصيلية مع الرسوم البيانية والتوصيات الشخصية"
            },
            {
              icon: Users,
              title: "DigComp 3.0",
              titleAr: "DigComp 3.0",
              description: "Based on the official European digital competence framework",
              descriptionAr: "بناءً على إطار الكفاءة الرقمية الأوروبي الرسمي"
            }
          ].map((feature, index) => (
            <Card
              key={index}
              className="p-6 text-center hover:shadow-medium transition-all duration-300 hover:scale-105"
            >
              <feature.icon className="h-10 w-10 text-primary mx-auto mb-4" />
              <h3 className="font-semibold text-foreground mb-1">{feature.title}</h3>
              <p className="text-xs text-muted-foreground font-tajawal mb-3" dir="rtl">{feature.titleAr}</p>
              <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                {feature.description}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed font-tajawal" dir="rtl">
                {feature.descriptionAr}
              </p>
            </Card>
          ))}
        </div>

        {/* Competence Areas */}
        <Card className="max-w-4xl mx-auto p-8 shadow-medium">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-2">
              Five Key Competence Areas
            </h2>
            <p className="text-2xl font-bold text-foreground font-tajawal" dir="rtl">
              خمسة مجالات كفاءة رئيسية
            </p>
          </div>
          <div className="space-y-6">
            {[
              {
                area: "Information & Data Literacy",
                areaAr: "محو الأمية المعلوماتية والبيانات",
                description: "Browse, search, evaluate and manage data and information",
                descriptionAr: "تصفح وبحث وتقييم وإدارة البيانات والمعلومات"
              },
              {
                area: "Communication & Collaboration",
                areaAr: "التواصل والتعاون",
                description: "Interact, share and collaborate through digital technologies",
                descriptionAr: "التفاعل والمشاركة والتعاون من خلال التقنيات الرقمية"
              },
              {
                area: "Digital Content Creation",
                areaAr: "إنشاء المحتوى الرقمي",
                description: "Create and edit digital content, respecting copyrights",
                descriptionAr: "إنشاء وتحرير المحتوى الرقمي مع احترام حقوق النشر"
              },
              {
                area: "Safety",
                areaAr: "السلامة",
                description: "Protect devices, personal data and privacy online",
                descriptionAr: "حماية الأجهزة والبيانات الشخصية والخصوصية عبر الإنترنت"
              },
              {
                area: "Problem Solving",
                areaAr: "حل المشكلات",
                description: "Solve technical problems and use digital tools creatively",
                descriptionAr: "حل المشكلات التقنية واستخدام الأدوات الرقمية بشكل إبداعي"
              }
            ].map((item, index) => (
              <div key={index} className="flex gap-4 p-4 rounded-lg bg-card/50 border border-border/50 hover:border-primary/50 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-lg">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground text-lg mb-1">{item.area}</h3>
                  <p className="text-sm text-muted-foreground mb-2">{item.description}</p>
                  <h3 className="font-semibold text-foreground font-tajawal mb-1" dir="rtl">{item.areaAr}</h3>
                  <p className="text-sm text-muted-foreground font-tajawal" dir="rtl">{item.descriptionAr}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Index;
